import { useMutation } from "@tanstack/react-query";
import { Download, FolderArchive, FolderOpen, Info } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { AttachmentList } from "./AttachmentList";
import { ordersApi } from "../api/orders";
import { useOrderProjectFiles } from "../hooks/useOrders";
import type { ManagedFileBrief, OrderProjectFolderNode } from "../types";

// Spłaszczenie drzewa do grup „ścieżka folderu → pliki" — tylko liście z plikami
// (kontenery nie niosą wpisów; puste foldery nie wnoszą nic do zamówienia).
function collectGroups(
  nodes: OrderProjectFolderNode[],
  path: string[] = [],
  acc: { slug: string; label: string; hint: string; entries: OrderProjectFolderNode["entries"] }[] = [],
) {
  for (const n of nodes) {
    const p = [...path, n.label];
    if (n.entries.length > 0) acc.push({ slug: n.slug, label: p.join(" / "), hint: n.source_hint, entries: n.entries });
    collectGroups(n.children, p, acc);
  }
  return acc;
}

/**
 * Sekcja „Pliki z projektu ZE" w zamówieniu — żywa projekcja udostępnionych, niepoufnych
 * folderów zakładki „Pliki" projektu (opcja przy „Zleć podwykonawcy"). Widoczna dla
 * podwykonawcy i zlecającego; renderuje się tylko, gdy faktycznie są udostępnione pliki.
 */
export function OrderProjectFilesSection({ orderId, enabled }: { orderId: string; enabled: boolean }) {
  const { data, isLoading } = useOrderProjectFiles(orderId, enabled);
  const zipM = useMutation({
    mutationFn: (folderSlug: string | null) =>
      ordersApi.downloadProjectFilesZip(orderId, folderSlug ?? undefined),
  });

  if (!enabled) return null;
  if (isLoading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-6 flex items-center gap-2 text-sm text-gray-400">
        <Spinner size="sm" /> Ładowanie plików z projektu ZE…
      </div>
    );
  }

  const groups = collectGroups(data?.folders ?? []);
  const totalFiles = groups.reduce((sum, g) => sum + g.entries.length, 0);
  // „Wyświetla się tylko gdy są udostępnione w ten sposób pliki".
  if (!data?.shared || totalFiles === 0) return null;

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-6">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5">
          <FolderOpen size={15} className="text-[var(--color-primary)]" />
          Pliki z projektu ZE ({totalFiles})
        </p>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[var(--color-primary)] disabled:opacity-60 transition-colors"
          title="Pobierz wszystkie udostępnione pliki ze strukturą folderów jako ZIP"
          disabled={zipM.isPending}
          onClick={() => zipM.mutate(null)}
        >
          {zipM.isPending && zipM.variables == null ? <Spinner size="sm" /> : <FolderArchive size={14} />}
          ZIP
        </button>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-3 flex items-start gap-1">
        <Info size={12} className="mt-0.5 shrink-0" />
        Podgląd na żywo folderów udostępnionych z projektu — zawartość może się zmieniać.
      </p>
      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.slug}>
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {g.label} ({g.entries.length})
              </p>
              <button
                type="button"
                className="p-0.5 text-gray-300 hover:text-[var(--color-primary)] disabled:opacity-40 transition-colors"
                title={`Pobierz „${g.label}" jako ZIP`}
                disabled={zipM.isPending}
                onClick={() => zipM.mutate(g.slug)}
              >
                <Download size={13} />
              </button>
            </div>
            <AttachmentList
              files={g.entries.map((e): ManagedFileBrief => ({
                id: e.managed_file_id,
                original_filename: e.filename,
                content_type: "",
                size_bytes: e.size_bytes,
                url: e.url,
              }))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
